import { useEffect, useState } from "react";
import { Trophy, Crown, Gem } from "lucide-react";
import { api } from "../lib/api";
import { fmtARS } from "../lib/utils-app";

const TABS = [
  { key: "amount", label: "Más gastado", testid: "ranking-tab-amount" },
  { key: "count", label: "Más recargas", testid: "ranking-tab-count" },
];
const MEDALS = {
  1: { color: "#fbbf24", glow: "rgba(251,191,36,0.35)", label: "TOP 1", lift: "md:-translate-y-6" },
  2: { color: "#d4d4d8", glow: "rgba(212,212,216,0.25)", label: "TOP 2", lift: "" },
  3: { color: "#cd7f32", glow: "rgba(205,127,50,0.3)", label: "TOP 3", lift: "" },
};
const ORDER = [2, 1, 3];

function Avatar({ r, color }) {
  const cls = "w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-4";
  if (r?.picture) return <img src={r.picture} alt="" referrerPolicy="no-referrer" className={cls} style={{ borderColor: color }} />;
  return (
    <div className={`${cls} bg-[#ff4500]/15 grid place-items-center font-display text-3xl text-[#ff4500]`} style={{ borderColor: color }}>
      {r ? r.display_name.slice(0, 1).toUpperCase() : "?"}
    </div>
  );
}

function PodiumCard({ pos, r }) {
  const m = MEDALS[pos];
  return (
    <div
      data-testid={`ranking-podium-${pos}`}
      className={`relative rounded-2xl border bg-[#0d0d0d] px-3 sm:px-4 pt-6 pb-5 text-center ${m.lift} ${pos === 1 ? "order-first md:order-none" : ""}`}
      style={{ borderColor: `${m.color}66`, boxShadow: `0 0 40px ${m.glow}` }}
    >
      <div className="text-[10px] tracking-[0.3em] font-semibold" style={{ color: m.color }}>{m.label}</div>
      <div className="relative mx-auto mt-9 w-fit">
        <Crown className="absolute -top-7 left-1/2 -translate-x-1/2 w-9 h-9 drop-shadow" style={{ color: m.color, fill: m.color }} data-testid={`ranking-crown-${pos}`} />
        <Avatar r={r} color={m.color} />
      </div>
      <div className="mt-3 text-white font-semibold truncate" data-testid={`ranking-name-${pos}`}>{r ? r.display_name : "Puesto libre"}</div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-white/5 py-2">
          <div className="text-[9px] uppercase tracking-widest text-neutral-500">Diamantes</div>
          <div className="font-display text-lg tracking-wider text-sky-300 flex items-center justify-center gap-1" data-testid={`ranking-diamonds-${pos}`}>
            <Gem className="w-3.5 h-3.5" />{r ? (r.total_diamonds || 0).toLocaleString("es-AR") : "—"}
          </div>
        </div>
        <div className="rounded-lg bg-white/5 py-2">
          <div className="text-[9px] uppercase tracking-widest text-neutral-500">Gastado</div>
          <div className="font-display text-lg tracking-wider text-[#ff4500]" data-testid={`ranking-amount-${pos}`}>{r ? fmtARS(r.total_amount) : "—"}</div>
        </div>
      </div>
      {r && <div className="mt-2 text-[10px] text-neutral-500">{r.total_count} {r.total_count === 1 ? "recarga" : "recargas"}</div>}
    </div>
  );
}

export default function WeeklyRanking() {
  const [sort, setSort] = useState("amount");
  const [rows, setRows] = useState(null);

  useEffect(() => {
    setRows(null);
    api.get("/ranking/weekly", { params: { sort } }).then(({ data }) => setRows(data.ranking)).catch(() => setRows([]));
  }, [sort]);

  return (
    <section id="ranking" data-testid="weekly-ranking" className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <div className="text-xs tracking-[0.3em] text-[#ff4500] flex items-center gap-2"><Trophy className="w-3.5 h-3.5" /> TOP SEMANAL</div>
      <h2 className="font-display text-4xl sm:text-5xl tracking-wider">Ranking de la semana</h2>
      <p className="text-sm text-neutral-400 mt-1">Los jugadores que más recargaron esta semana. Se reinicia cada lunes.</p>
      <div className="mt-5 inline-flex rounded-full border border-white/10 bg-[#0d0d0d] p-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={t.testid}
            onClick={() => setSort(t.key)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold ${sort === t.key ? "bg-[#ff4500] text-black" : "text-neutral-300 hover:text-white"}`}
            style={{ transition: "background-color 160ms ease, color 160ms ease" }}
          >
            {t.label}
          </button>
        ))}
      </div>
      {rows === null ? (
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
          {[0, 1, 2].map((i) => <div key={i} className="h-72 rounded-2xl bg-white/5 animate-pulse" />)}
        </div>
      ) : (
        <>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4 max-w-4xl mx-auto items-end">
            {ORDER.map((pos) => <PodiumCard key={pos} pos={pos} r={rows[pos - 1]} />)}
          </div>
          {rows.length === 0 && (
            <p data-testid="ranking-empty" className="mt-6 text-center text-sm text-neutral-400">Todavía no hay recargas esta semana. ¡Sé el primero del ranking!</p>
          )}
        </>
      )}
    </section>
  );
}
